import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { remove } from 'lodash';
import { Grid2, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createStudyReportTemplate, editStudyReportTemplate } from './_apiStudyReportTemplate';
import useValidationSchema from './_formStudyReportTemplate';
import { IStudyReportTemplate, IStudyReportTemplateForm } from './_types';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import FormInput from 'components/Form/Input/Input';
import { Papeer } from 'components/Papeer/Papeer';
import useAlerts from 'components/Alerts/useAlerts';
import { EntityButtons } from 'components/Form/EntityButtons/EntityButtons';

const StudyReportTemplateForm: React.FC<IStudyReportTemplateForm> = ({
  toggleDialog,
  keyValues,
  id,
  row,
  loadEntity,
}) => {
  const { t } = useTranslation('StudyReportTemplates');
  const { compactMode } = useAppInfo();
  const { toggleLoader } = useAppGlobals();
  const { addErrorAlert, addSuccessAlert } = useAlerts();
  const [entity, setEntity] = useState<IStudyReportTemplate>();
  const [prepareKeyValues, setPrepareKeyValues] = useState<{
    keywords: string[];
    contents: string[];
  }>(keyValues);

  const { StudyReportTemplateFormSchema } = useValidationSchema(prepareKeyValues);

  const methods = useForm<IStudyReportTemplate>({
    resolver: yupResolver(StudyReportTemplateFormSchema),
    defaultValues: { keyword: '', content: '' },
  });
  const { handleSubmit, reset, register } = methods;

  const isCreating = id === 'create';

  const onSubmit = handleSubmit(async (values) => {
    toggleLoader();
    const fn = isCreating ? createStudyReportTemplate : editStudyReportTemplate;
    const response = await fn(values);
    if (response === true) {
      addSuccessAlert(t('saved'));
      loadEntity();
      toggleDialog(false);
    } else {
      addErrorAlert(t('errorSaving'));
    }
    toggleLoader(false);
  });

  const getEntity = async () => {
    toggleLoader();
    try {
      setEntity(row);
      reset(row);
    } catch (e) {
      console.debug(e);
    }
    toggleLoader(false);
  };

  useEffect(() => {
    getEntity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (entity) {
      const keywords = [...keyValues.keywords];
      const contents = [...keyValues.contents];
      remove(keywords, (item) => item === entity.keyword);
      remove(contents, (item) => item === entity.content);
      setPrepareKeyValues({ keywords, contents });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity]);

  return (
    <>
      {entity || isCreating ? (
        <FormProvider {...methods}>
          <form onSubmit={onSubmit}>
            {entity && <input {...register('id')} type="hidden" />}
            <Grid2 container={true} spacing={0}>
              <Grid2 size={12}>
                <FormInput name="keyword" label={t('keyword')} type="text" required={true} />
              </Grid2>
              <Grid2 size={12}>
                <FormInput
                  name="content"
                  label={t('content')}
                  type="text"
                  required={true}
                  multiline={true}
                  rows={8}
                />
              </Grid2>
            </Grid2>
            <EntityButtons
              toggleDialog={toggleDialog}
              otherStyles={{ marginTop: compactMode ? 8 : 16 }}
            />
          </form>
        </FormProvider>
      ) : (
        <Papeer>
          <Typography>{t('studyReportTemplateNotFound')}</Typography>
        </Papeer>
      )}
    </>
  );
};

export default StudyReportTemplateForm;
